import { createId as id } from "@paralleldrive/cuid2";
import { z } from "zod";

export type Value<T> = T[keyof T];

export type Mutable<T> = {
  -readonly [K in keyof T]: T[K];
};

export type Optional<T, K extends keyof T> = Omit<T, K> & Partial<Pick<T, K>>;

/**
 * Use this to iterate over an object and get the keys and values
 */
export const entries = <T extends Record<string, unknown>>(obj: T): readonly [keyof T, T[keyof T]][] => {
  // biome-ignore lint/plugin/no-type-assertions: Object.entries loses key literal types — assertion restores them
  return Object.entries(obj) as readonly [keyof T, T[keyof T]][];
};

/**
 * Use this to generate typesafe objects from an array of keys and values
 */
export const fromEntries: <K extends PropertyKey, V>(entries: Iterable<readonly [K, V]>) => Record<K, V> =
  Object.fromEntries;

export const notNullish = <T>(item: T): item is NonNullable<T> => item !== null && item !== undefined;

/**
 * Checks that the input is not false, null, or undefined
 * Made for checking if a React node will render, but might be otherwise useful
 */
export const isTruthy = <T>(item: T): item is Exclude<T, false | null | undefined> =>
  item !== false && item !== null && item !== undefined;

/**
 * Use this to filter out nullish values from an array and get a good type result
 */
export const filterOutNullish = <T>(item: T): item is NonNullable<T> => item !== null && item !== undefined;

/**
 * Use this to parse an object with a Zod schema and get a typed result. Using just schema.parse(obj) is problematic
 * because obj is of type unknown and the return type is not linked to the input type. This function is essentially
 * for stripping away excess properties while ensuring that the input matches the schema.
 */
export const parseTyped = <TSchema extends z.ZodType, TReturn extends z.infer<TSchema>>(
  schema: TSchema,
  obj: TReturn,
): TReturn => {
  // biome-ignore lint/plugin/no-type-assertions: schema.parse returns unknown in Zod v4 — TReturn is already constrained to match the schema
  return schema.parse(obj as unknown) as TReturn;
};

/**
 * Given a list return a new list with all duplicates removed
 *
 * Can handle objects given a comparison function that compares two objects for equality
 */
export const dedupeList = <T>(list: T[], comparisonFn?: (a: T, b: T) => boolean): T[] => {
  if (!comparisonFn) {
    const set = new Set(list);
    return Array.from(set);
  }
  return list.filter((item, index) => {
    return list.findIndex((otherItem) => comparisonFn(item, otherItem)) === index;
  });
};

/**
 * Group items into a Map by a key function.
 *
 * @example
 * const byId = groupByKey(items, (x) => x.id);
 * byId.get("abc") // => T[]
 */
export const groupByKey = <T, K extends string>(items: readonly T[], getKey: (item: T) => K): Map<K, T[]> => {
  const grouped = defaultMap<K, T[]>(() => []);
  for (const item of items) {
    grouped[getKey(item)].push(item);
  }
  return new Map(entries(grouped));
};

export const interleaveLists = <T>(lists: readonly (readonly T[])[]): T[] => {
  const longest = Math.max(0, ...lists.map((list) => list.length));
  return Array.from({ length: longest }, (_, index) => lists.flatMap((list) => list.slice(index, index + 1))).flat();
};

export const sum = (arr: number[]) => arr.reduce((acc, val) => acc + val, 0);

export const max = <T extends number | Date>(...arr: (T | undefined | null)[]) => {
  const filtered = arr.filter((item) => item !== undefined && item !== null);
  if (filtered.length === 0) {
    return undefined;
  }
  return filtered.reduce((acc, val) => (val > acc ? val : acc));
};

/**
 * Partitions a list based on a conditional function
 *
 * Returns a tuple of two lists, the first list contains all items that
 * satisfy the condition, the second list contains all items that do not
 *
 * @example
 * const [even, odd] = partition([1, 2, 3, 4], (n) => n % 2 === 0);
 * // even = [2, 4]
 * // odd = [1, 3]
 */
export const partition = <T>(input: T[], fn: (item: T) => boolean): readonly [T[], T[]] => {
  const a: T[] = [];
  const b: T[] = [];
  input.forEach((item) => {
    if (fn(item)) {
      a.push(item);
    } else {
      b.push(item);
    }
  });
  return [a, b];
};

type GroupedList<T, K extends keyof T> = {
  values: T[];
  groupName: T[K];
};

/**
 * Partitions a list into different lists based on a list of values for a specific value
 *
 * Returns a list of objects that contain the values and the specific key value they relate to
 *
 * @example
 * const groupedList = splitListIntoGroups({ input: [{ value: "DONE" }, { value: "DONE" }, { value: "TODO" }, { value: "IN_PROGRESS" }], groupNames: ["DONE", "IN_PROGRESS"], key: "value" });
 * groupedList = [{ values: [{ value: "DONE" }, { value: "DONE" }], groupName: "DONE" }, { values: [{ value: "IN_PROGRESS" }], groupName: "IN_PROGRESS" }]
 */
export const splitListIntoGroups = <T, K extends keyof T>({
  input,
  groupNames,
  key,
}: {
  input: T[];
  groupNames: T[K][];
  key: K;
}): readonly GroupedList<T, K>[] => {
  const ret: GroupedList<T, K>[] = [];
  for (const groupName of groupNames) {
    const values: T[] = [];
    for (const item of input) {
      if (item[key] === groupName) {
        values.push(item);
      }
    }
    ret.push({ values, groupName });
  }

  return ret;
};

/**
 * Force an operation like `{ a: 0 } & { b: 1 }` to be computed so that it displays `{ a: 0; b: 1 }`.
 *
 * Also works for some non-intersections, e.g. `keyof SomeObj` => `"a" | "b" | ...`
 */
export type evaluate<t> = { [k in keyof t]: t[k] } & unknown;

export const defaultMap = <K extends string | symbol, V>(defaultValue: () => V): Record<K, V> => {
  // biome-ignore lint/plugin/no-type-assertions: Proxy target starts empty; keys are populated lazily on get
  return new Proxy({} as Record<K, V>, {
    get: (target, name: K) => (name in target ? target[name] : (target[name] = defaultValue())),
  });
};

/**
 * Util for checking if an item is one of a list of strings
 * Avoids having to do ["foo", "bar"].includes("foo")
 */
export const isOneOf = <T extends string | null | undefined, U extends string | null | undefined>(
  v: T,
  values: readonly U[],
): v is T & U => {
  // biome-ignore lint/plugin/no-type-assertions: widening to string is safe — includes() only does equality checks
  return values.includes(v as string as U);
};

// TypeError rather than a Remi error: this is a leaf package and cannot import @remi/error.
export const assertNever = (value: never, message: string): never => {
  throw new TypeError(`${message}: ${String(value)}`);
};

/**
 * Use this to iterate over an object and get the keys and values as options
 */
export const toOptions = <T extends Record<string, unknown>>(obj: T): { value: keyof T; label: T[keyof T] }[] => {
  return entries(obj).map(([key, value]) => ({ value: key, label: value }));
};

/**
 * Filter an array of objects using a Zod schema to get typed results
 */
export const filterSchema = <T extends U, U>(arr: U[], schema: z.ZodType<T>): T[] => {
  return arr
    .map((item) => {
      const result = schema.safeParse(item as unknown);
      return result.success ? result.data : undefined;
    })
    .filter((item) => item !== undefined);
};

interface DroppedFields {
  droppedFields: string[];
  droppedIssues: z.core.$ZodIssue[];
}

type DroppingParseResult<T> =
  | ({ success: true; data: T } & DroppedFields)
  | ({ success: false; error: z.ZodError } & DroppedFields);

/**
 * Re-parses after each replacement: Zod skips refines behind a type error, and a refine like "phone or email" only
 * fails once a field is cleared.
 */
export const safeParseDroppingInvalidFields = <T>(
  schema: z.ZodType<T>,
  input: Readonly<Record<string, unknown>>,
  fallbacks: Readonly<Record<string, unknown>>,
): DroppingParseResult<T> => {
  const parse = (candidate: Readonly<Record<string, unknown>>, dropped: DroppedFields): DroppingParseResult<T> => {
    const parsed = schema.safeParse(candidate as unknown);
    if (parsed.success) {
      return { success: true, data: parsed.data, ...dropped };
    }
    const fields = [...new Set(parsed.error.issues.map((issue) => issue.path[0]))];
    const isDroppable = (field: unknown): field is string =>
      typeof field === "string" && Object.hasOwn(fallbacks, field) && !Object.is(candidate[field], fallbacks[field]);
    if (!fields.every(isDroppable)) {
      return { success: false, error: parsed.error, ...dropped };
    }
    return parse(
      { ...candidate, ...Object.fromEntries(fields.map((field) => [field, fallbacks[field]])) },
      {
        droppedFields: [...dropped.droppedFields, ...fields],
        droppedIssues: [...dropped.droppedIssues, ...parsed.error.issues],
      },
    );
  };
  return parse(input, { droppedFields: [], droppedIssues: [] });
};

/**
 * Creates a strongly unique id
 */
export const createId = () => id();

/**
 * Remi API key versions
 */
export const ApiKeyVersionSchema = z.literal("RP1");
export type ApiKeyVersion = z.infer<typeof ApiKeyVersionSchema>;

export const YesNoOptions = {
  YES: "Yes",
  NO: "No",
} as const;

export type Expect<T extends true> = T;
type Equal<X, Y> = (<T>() => T extends X ? 1 : 2) extends <T>() => T extends Y ? 1 : 2 ? true : false;
export type EqualWithDiff<A, B> = Equal<A, B> extends true ? true : { missing: Exclude<A, B>; extra: Exclude<B, A> };

type OmittedHistoryField = "id" | "createdAt" | "updatedAt";

/**
 * Keys of an entity that are eligible for history tracking — every field except the immutable
 * primary key and audit timestamps, none of which can meaningfully change. Pair with
 * `Expect<EqualWithDiff<HistoryFieldEnum, EntityHistoryField<T>>>` in a per-entity history field
 * file to fail compilation when the tracked fields drift from the schema.
 */
export type EntityHistoryField<T> = Exclude<keyof T, OmittedHistoryField>;

/**
 * Helper type to check if an array has duplicate values.
 * Returns `true` if duplicates exist, `false` otherwise.
 *
 * @example
 * ```ts
 * type _test1 = Expect<HasDuplicates<['a', 'b', 'c']> extends false ? true : false>; // ✅ passes
 * type _test2 = Expect<HasDuplicates<['a', 'b', 'a']> extends true ? true : false>; // ✅ passes
 * type _test3 = Expect<HasDuplicates<[1, 2, 3, 2]> extends true ? true : false>; // ✅ passes
 * ```
 */
export type HasDuplicates<T extends readonly unknown[]> = T extends readonly [infer First, ...infer Rest]
  ? First extends Rest[number]
    ? true
    : HasDuplicates<Rest>
  : false;

export const stringToNumber = (s?: string) => {
  if (!s) {
    return undefined;
  }
  const n = Number(s);
  if (isNaN(n)) {
    return undefined;
  }
  return n;
};

export const splitIntoBatches = <T>(items: T[], batchSize: number): T[][] => {
  const batches: T[][] = [];
  for (let i = 0; i < items.length; i += batchSize) {
    batches.push(items.slice(i, i + batchSize));
  }
  return batches;
};

interface BatchProcessingOptions<T> {
  items: T[];
  batchSize?: number;
  delayMs?: number;
  processItem: (item: T) => Promise<void>;
}

interface BatchProcessingError<T> {
  item: T;
  error: unknown;
}

interface BatchProcessingResult<T> {
  successful: T[];
  failed: BatchProcessingError<T>[];
}

export async function processBatch<T>({
  items,
  batchSize = 5,
  delayMs = 0,
  processItem,
}: BatchProcessingOptions<T>): Promise<BatchProcessingResult<T>> {
  const successful: T[] = [];
  const failed: BatchProcessingError<T>[] = [];

  for (let i = 0; i < items.length; i += batchSize) {
    const batch = items.slice(i, Math.min(i + batchSize, items.length));

    await Promise.allSettled(
      batch.map(async (item) => {
        try {
          await processItem(item);
          successful.push(item);
        } catch (error) {
          failed.push({ item, error });
        }
      }),
    );

    if (i + batchSize < items.length) {
      await new Promise((resolve) => setTimeout(resolve, delayMs));
    }
  }

  return { successful, failed };
}

export type PossiblyUndefined<T> = {
  [K in keyof T]: T[K] | undefined;
};

export type ObjectsToString<T> = {
  [K in keyof T]: NonNullable<T[K]> extends object
    ? T[K] extends null | undefined
      ? string | Extract<T[K], null | undefined>
      : string
    : T[K];
};

export const hasStringProperty = <TObj, TKey extends PropertyKey>(
  o: TObj,
  key: TKey,
): o is Exclude<TObj, null | undefined> & Record<TKey, string> => {
  return (
    typeof o === "object" &&
    o !== null &&
    Object.hasOwn(o, key) &&
    // biome-ignore lint/plugin/no-type-assertions: bypass lint check for type assertions, can't narrow based on string parameter
    typeof (o as Record<PropertyKey, unknown>)[key] === "string"
  );
};

type AddOne<N extends number> = [...BuildTuple<N>, unknown]["length"];

// Build a tuple of length N
type BuildTuple<N extends number, T extends unknown[] = []> = T["length"] extends N
  ? T
  : BuildTuple<N, [...T, unknown]>;

// Integers in the range [1, N]
export type RangeOneToN<N extends number> =
  Exclude<keyof BuildTuple<N>, keyof []> extends infer K
    ? K extends `${infer Num extends number}`
      ? AddOne<Num>
      : never
    : never;

export const getLatestByCompletedAt = <T extends { completedAt: Date | null }>(input: T[]): T | null => {
  let latest: T | null = null;

  for (const current of input) {
    if (!current.completedAt) {
      continue;
    }

    if (!latest?.completedAt || current.completedAt > latest.completedAt) {
      latest = current;
    }
  }

  return latest;
};

// Money is compared/stored in integer cents to avoid floating-point drift.
export const dollarsToCents = (dollars: number): number => Math.round(dollars * 100);

// Inverse of dollarsToCents - back to dollars at a Float boundary (QBO/Prisma columns).
export const centsToDollars = (cents: number): number => cents / 100;

// Round a dollar amount to the nearest cent - for sums over Float dollar columns where
// accumulated drift would otherwise leave a sub-cent tail.
export const roundToCents = (amount: number): number => Math.round(amount * 100) / 100;

// Trailing segment only; the full provider URL embeds account identifiers and signed-URL secrets.
export const mediaIdentifier = (url: string | undefined): string => {
  const [withoutQuery] = (url ?? "").split("?");
  return withoutQuery?.split("/").pop() || "unknown";
};

// Free-text field shared by a client optimistic update and the server write, so
// both must agree that whitespace-only input clears the column rather than
// storing a blank string.
export const trimToNull = (value: string): string | null => {
  const trimmed = value.trim();
  return trimmed.length === 0 ? null : trimmed;
};

// Leaf package: the caller supplies the rejection so the domain error stays in the domain.
export const withBudget = async <T>(promise: Promise<T>, budgetMs: number, onTimeout: () => Error): Promise<T> => {
  let timer: NodeJS.Timeout | undefined;
  try {
    return await Promise.race([
      promise,
      new Promise<never>((_, reject) => {
        timer = setTimeout(() => reject(onTimeout()), budgetMs);
      }),
    ]);
  } finally {
    clearTimeout(timer);
  }
};

export const raceWithTimeout = async <T>(
  promise: Promise<T>,
  timeoutMs: number,
): Promise<{ timedOut: false; value: T } | { timedOut: true }> => {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      promise.then((value) => ({ timedOut: false as const, value })),
      new Promise<{ timedOut: true }>((resolve) => {
        timer = setTimeout(() => resolve({ timedOut: true }), timeoutMs);
      }),
    ]);
  } finally {
    clearTimeout(timer);
  }
};

// Acronym-aware: an uppercase run stays one word, so "nonRemiPVInstall" → "non-remi-pv-install".
export const convertToKebabCase = (value: string): string =>
  value
    .replace(/([a-z\d])([A-Z])/g, "$1-$2")
    .replace(/([A-Z]+)([A-Z][a-z])/g, "$1-$2")
    .toLowerCase();
